import LeaseApiService from './apiService';
import { endPoint } from '../EndPoints/endPoint';

/**
 * @typedef {{ value: string, text: string }} DropdownItem
 * @typedef {{ unitNo: string, leaseNo: string }} UnitItem
 * @typedef {{ recordId: string, leaseNo: string, propertyNo: string, propertyName: string, unitNo: string, tenantNo: string,
 *   tenantName: string, receiptNo: string, installmentDesc: string, amountPaid: number, voucherNo: string, paymentDate: string,
 *   paymentMethod: string, chequeStatus: string|null, chequeNo: string, chequeDate: string, hasAccess: boolean }} ReceiptLine
 * @typedef {{ lines: ReceiptLine[], totalPaid: number|null }} ReceiptLinesResult
 * @typedef {{ unallocatedId: string, leaseNo: string, receiptType: string, propertyName: string, unitNo: string, customerNo: string,
 *   customerName: string, receiptNo: string, type: string, unallocatedAmount: number, paymentDate: string, propertyId: string,
 *   remarks: string }} UnallocatedRow
 * @typedef {{ leaseNo: string, items: { unallocatedId: string, remarks: string }[] }} UpdateRemarksRequest
 * @typedef {{ recordId: string, transactionType: string, amount: number, remarks: string, refundAccountCode: string|null }} ReverseItem
 * @typedef {{ reverseDate: string, items: ReverseItem[] }} ReverseReceiptsRequest
 * @typedef {{ rrNo: string, linesReversed: number, totalAmount: number }} ReverseResult
 * @typedef {{ ok: boolean, status: number, message: string, result: any, dropdowns: any }} ApiResult
 */

const api = new LeaseApiService();
const http = api.axiosInstance;

const GENERIC_ERROR = 'Something went wrong. Please try again.';
// The backend allows up to 20 minutes for a reversal; only that request gets this timeout.
export const REVERSE_TIMEOUT_MS = 20 * 60 * 1000;

/** 400 carries a readable validation message; anything else (500, network) gets the generic one. */
const errorMessage = (status, serverMessage) => (status === 400 ? serverMessage || 'Invalid request.' : GENERIC_ERROR);

/** Normalises BaseResponse success and failure into one shape so callers never touch axios errors. */
const call = async (request) => {
    try {
        const res = await request();
        const body = res?.data;
        const status = body?.statusCode ?? res?.status;
        const ok = status >= 200 && status < 300;
        return {
            ok,
            status,
            message: ok ? body?.message || '' : errorMessage(status, body?.message),
            result: body?.result ?? body?.data ?? null,
            dropdowns: body?.dropdowns ?? null,
        };
    } catch (error) {
        const status = error?.response?.status || 0;
        return { ok: false, status, message: errorMessage(status, error?.response?.data?.message), result: null, dropdowns: null };
    }
};

const withQuery = (params) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && `${value}`.trim() !== '') q.append(key, value);
    });
    const s = q.toString();
    return s ? `?${s}` : '';
};

/** @param {string[]} dropdownTypes */
export const getDropdowns = (dropdownTypes, showAll = false) =>
    call(() => http.post(endPoint.LRR_DROPDOWNS, { dropdownTypes, showAll }));

export const getUnits = ({ propertyNo, showAll = false }) =>
    call(() => http.get(endPoint.LRR_UNITS + withQuery({ propertyNo, showAll })));

/** At least one of leaseNo / receiptNo is required. */
export const getReceiptLines = ({ leaseNo, receiptNo }) =>
    call(() => http.get(endPoint.LRR_RECEIPT_LINES + withQuery({ leaseNo, receiptNo })));

export const getUnallocatedPayments = (leaseNo) =>
    call(() => http.get(`${endPoint.LRR_UNALLOCATED}${encodeURIComponent(leaseNo)}`));

/** @param {UpdateRemarksRequest} body */
export const updateUnallocatedRemarks = (body) => call(() => http.put(endPoint.LRR_UPDATE_REMARKS, body));

/** @param {ReverseReceiptsRequest} body */
export const reverseReceipts = (body) =>
    call(() => http.post(endPoint.LRR_REVERSE, body, { timeout: REVERSE_TIMEOUT_MS }));

const leaseReverseReceiptApi = {
    getDropdowns, getUnits, getReceiptLines, getUnallocatedPayments, updateUnallocatedRemarks, reverseReceipts,
};

export default leaseReverseReceiptApi;
